/**
 * Captures UTM parameters from the URL and stores them in a first-party cookie.
 * This runs before any authentication redirects to preserve campaign tracking.
 * Each organization gets its own cookie to prevent cross-organization attribution.
 */

import { matchPath } from "react-router-dom";
import {
  MAX_ORGANIZATION_PATH_LENGTH,
  MIN_ORGANIZATION_PATH_LENGTH,
  ORGANIZATION_PATH_PATTERN_CASE_INSENSITIVE,
} from "src/schemas/Organization";
import { ORGANIZATION } from "src/services/url/constants";
import { z } from "zod";

const UTM_COOKIE_PREFIX = "avela_utm_params";
const UTM_COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

// Security: Maximum lengths to prevent cookie overflow and database issues
const MAX_UTM_PARAM_LENGTH = 255; // Maximum length for individual UTM parameter (matches DB column size)
const MAX_COOKIE_SIZE = 3500; // Maximum cookie size in bytes (under 4KB browser limit)
const MAX_TOTAL_UTM_LENGTH = 2000; // Maximum total length of all UTM parameters combined

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_custom",
] as const;

export type UTMParams = {
  [K in (typeof UTM_PARAMS)[number]]?: string;
};

/**
 * Zod schema for validating individual UTM parameter values
 * - Allows only alphanumeric, spaces, hyphens, underscores, periods, and common URL-safe chars
 * - Enforces maximum length limits
 * - Trims whitespace
 * - Prevents XSS, SQL injection, and other attacks
 */
const UTMValueSchema = z
  .string()
  .trim()
  .max(
    MAX_UTM_PARAM_LENGTH,
    `UTM parameter exceeds maximum length of ${MAX_UTM_PARAM_LENGTH} characters`
  )
  .regex(
    /^[a-zA-Z0-9\s\-_./:=%&?+@!*(),']+$/,
    "UTM parameter contains unsafe characters"
  );

/**
 * Validates and sanitizes a UTM parameter value using Zod
 * Returns null for invalid input, sanitized string for valid input
 */
function sanitizeUTMValue(value: string | null | undefined): string | null {
  if (!value || typeof value !== "string") {
    return null;
  }

  const result = UTMValueSchema.safeParse(value);

  if (!result.success) {
    const errorMessage = result.error.issues[0]?.message ?? "Validation failed";
    console.warn(
      `UTM parameter validation failed: ${errorMessage} - Value: ${value.substring(
        0,
        50
      )}...`
    );
    return null;
  }

  return result.data;
}

/**
 * Zod schema for validating organization paths
 * Uses shared constants from Organization schema
 */
const OrganizationPathSchema = z
  .string()
  .trim()
  .min(MIN_ORGANIZATION_PATH_LENGTH, "Organization path is too short")
  .max(MAX_ORGANIZATION_PATH_LENGTH, "Organization path is too long")
  .regex(
    ORGANIZATION_PATH_PATTERN_CASE_INSENSITIVE,
    "Organization path contains unsafe characters"
  );

/**
 * Validates organization path using Zod
 * Returns null for invalid input, validated string for valid input
 *
 * Note: This validation is case-insensitive to handle URL reading,
 * but the database stores organization paths in lowercase.
 * See: src/schemas/Organization.ts for the shared validation constants
 */
function validateOrganizationPath(
  organization: string | undefined
): string | null {
  if (!organization || typeof organization !== "string") {
    return null;
  }

  const result = OrganizationPathSchema.safeParse(organization);

  if (!result.success) {
    const errorMessage = result.error.issues[0]?.message ?? "Validation failed";
    console.warn(
      `Invalid organization path: ${errorMessage} - Value: ${organization}`
    );
    return null;
  }

  return result.data;
}

/**
 * Security: Validates that parsed cookie data matches expected UTMParams structure
 * - Ensures all values are strings or undefined
 * - Removes unexpected properties
 * - Prevents prototype pollution
 */
function validateUTMParamsStructure(data: unknown): UTMParams | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return null;
  }

  const validated: UTMParams = {};
  let hasValidParam = false;

  for (const param of UTM_PARAMS) {
    const value = (data as Record<string, unknown>)[param];

    if (value !== undefined && value !== null) {
      // Only accept string values
      if (typeof value === "string") {
        const sanitized = sanitizeUTMValue(value);
        if (sanitized) {
          validated[param] = sanitized;
          hasValidParam = true;
        }
      } else {
        console.warn(
          `UTM parameter ${param} has invalid type: ${typeof value}, ignoring`
        );
      }
    }
  }

  return hasValidParam ? validated : null;
}

/**
 * Gets the cookie name for a specific organization
 * Security: Organization is validated before being used in cookie name
 * Returns null if validation fails instead of throwing to prevent errors in getUTMParamsFromCookie
 */
function getCookieName(organization: string): string | null {
  const validated = validateOrganizationPath(organization);
  if (!validated) {
    return null;
  }
  return `${UTM_COOKIE_PREFIX}_${validated}`;
}

/**
 * Gets UTM parameters from the current URL
 * Security: Sanitizes all values before storing
 */
function getUTMParamsFromURL(): UTMParams {
  const searchParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};
  let totalLength = 0;

  for (const param of UTM_PARAMS) {
    const value = searchParams.get(param);
    const sanitized = sanitizeUTMValue(value);

    if (sanitized) {
      totalLength += sanitized.length;

      // Security: Enforce total length limit
      if (totalLength > MAX_TOTAL_UTM_LENGTH) {
        console.warn(
          `Total UTM parameters exceed maximum length of ${MAX_TOTAL_UTM_LENGTH} characters, ignoring remaining parameters`
        );
        break;
      }

      utmParams[param] = sanitized;
    }
  }

  return utmParams;
}

/**
 * Extracts the organization from the current URL pathname
 * Uses the same matching logic as useOrganizationPath but without React hook dependency
 * Expected format: /:organization/... where organization is the first path segment
 * Returns undefined if no organization is found or if it's an admin/auth path
 * Security: Validates organization path before returning
 */
function getOrganizationFromPath(): string | undefined {
  const pathname = window.location.pathname;

  // Explicitly skip admin, auth, and root paths before matching
  if (
    pathname.startsWith("/admin") ||
    pathname === "/" ||
    pathname.startsWith("/auth")
  ) {
    return undefined;
  }

  const match = matchPath(
    { path: `/${ORGANIZATION}`, end: false, caseSensitive: false },
    pathname
  );

  const organization = match?.params?.organization;
  return validateOrganizationPath(organization) || undefined;
}

/**
 * Stores UTM parameters in an organization-specific first-party cookie
 * Security: Validates cookie size before storing
 */
function saveUTMParamsToCookie(params: UTMParams, organization: string): void {
  if (Object.keys(params).length === 0) {
    return; // No UTM params to save
  }

  const cookieValue = JSON.stringify(params);
  const encodedValue = encodeURIComponent(cookieValue);

  // Security: Validate cookie size before storing
  if (encodedValue.length > MAX_COOKIE_SIZE) {
    console.error(
      `UTM cookie size (${encodedValue.length} bytes) exceeds maximum of ${MAX_COOKIE_SIZE} bytes, not storing`
    );
    return;
  }

  try {
    const cookieName = getCookieName(organization);

    if (!cookieName) {
      console.error(
        `Invalid organization path, cannot save UTM cookie: ${organization}`
      );
      return;
    }

    // Set cookie with SameSite=Lax for cross-page persistence
    // Note: Secure flag is only included in production (HTTPS)
    const isSecure = window.location.protocol === "https:";
    const securePart = isSecure ? "; Secure" : "";
    document.cookie = `${cookieName}=${encodedValue}; max-age=${UTM_COOKIE_MAX_AGE}; path=/; SameSite=Lax${securePart}`;

    console.debug("UTM parameters saved to cookie:", {
      organization,
      ...params,
    });
  } catch (error) {
    console.error("Failed to save UTM cookie:", error);
  }
}

/**
 * Retrieves UTM parameters from the organization-specific cookie
 * Security: Validates cookie size, structure, and content before returning
 *
 * @param organization - The organization path to look up UTM params for
 * @returns UTM parameters if found and valid, null otherwise
 *
 * @example
 * ```typescript
 * import { getUTMParamsFromCookie } from 'src/utils/utmTracking';
 *
 * const utmParams = getUTMParamsFromCookie('philasd');
 * if (utmParams) {
 *   console.log('Campaign source:', utmParams.utm_source);
 *   console.log('Campaign medium:', utmParams.utm_medium);
 *   // Use these values for analytics, form fields, etc.
 * }
 * ```
 */
export function getUTMParamsFromCookie(organization: string): UTMParams | null {
  try {
    const cookieName = getCookieName(organization);

    if (!cookieName) {
      console.warn(`Invalid organization path: ${organization}`);
      return null;
    }

    const cookies = document.cookie.split("; ");
    const utmCookie = cookies.find((cookie) =>
      cookie.startsWith(`${cookieName}=`)
    );

    if (!utmCookie) {
      return null;
    }

    const parts = utmCookie.split("=");
    const encodedValue = parts.slice(1).join("="); // Handle values with '=' in them

    if (!encodedValue) {
      return null;
    }

    // Security: Check size before decoding
    if (encodedValue.length > MAX_COOKIE_SIZE) {
      console.error(
        `UTM cookie size exceeds maximum of ${MAX_COOKIE_SIZE} bytes, ignoring`
      );
      return null;
    }

    const cookieValue = decodeURIComponent(encodedValue);

    // Security: Validate JSON structure before parsing
    if (cookieValue.length > MAX_COOKIE_SIZE) {
      console.error("Decoded UTM cookie exceeds size limit");
      return null;
    }

    const parsed = JSON.parse(cookieValue);

    // Security: Validate structure and sanitize values
    return validateUTMParamsStructure(parsed);
  } catch (error) {
    console.error("Failed to parse UTM cookie:", error);
    return null;
  }
}

/**
 * Deletes the organization-specific UTM cookie
 * This should be called after successfully tracking a conversion to ensure
 * UTM parameters are only used once for attribution.
 *
 * @param organization - The organization path whose cookie should be deleted
 *
 * @example
 * ```typescript
 * import { deleteUTMCookie } from 'src/utils/utmTracking';
 *
 * // After tracking conversion
 * deleteUTMCookie('philasd');
 * ```
 */
export function deleteUTMCookie(organization: string): void {
  try {
    const cookieName = getCookieName(organization);

    if (!cookieName) {
      console.warn(
        `Invalid organization path, cannot delete UTM cookie: ${organization}`
      );
      return;
    }

    // Set expiration to the past to delete the cookie
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    console.debug("UTM cookie deleted for organization:", organization);
  } catch (error) {
    console.error("Failed to delete UTM cookie:", error);
  }
}

/**
 * Main function to capture UTM parameters and store them in an organization-specific cookie.
 * This should be called as early as possible in the application lifecycle,
 * before any authentication redirects.
 */
export function captureUTMParams(): void {
  const urlParams = getUTMParamsFromURL();

  // Only save if we found UTM parameters in the URL
  if (Object.keys(urlParams).length > 0) {
    const organization = getOrganizationFromPath();

    // Only save if we have an organization in the path
    if (organization) {
      saveUTMParamsToCookie(urlParams, organization);
    } else {
      console.debug(
        "No organization found in path, skipping UTM tracking",
        urlParams
      );
    }
  }
}
